import { liteClient as algoliasearch } from "algoliasearch/lite";
import cx from "classnames";
import type { Hit } from "instantsearch.js";
import { ReactNode, useEffect, useState } from "react";
import {
  Configure,
  InstantSearch,
  useInfiniteHits,
  useInstantSearch,
  useSortBy,
} from "react-instantsearch";

import { Button } from "@/atoms/button/Button";
import { useFacet } from "@/hooks/useFacet";
import type { AlgoliaSearchResult } from "@/models/AlgoliaSearchResult";
import {
  convertListingTypeToProductType,
  type ListingTypeOptions,
} from "@/utils/productTypes";
import { Models } from "api/types";
import DownChevron from "atoms/icon/icons/chevron_down_small.svg";
import { TextButton } from "atoms/text-button/TextButton";
import { ModalSelectorFilterList } from "organisms/selection-modals/modal-selector-filter-list/ModalSelectorFilterList";
import { useListingFilterAndSort } from "pages/root/shop/useListingFilterAndSort";

import {
  type ListingSortOptions,
  sortOptions,
  typeOptions,
} from "./FilterAndSortListings";

interface Props {
  platformId: string;
  types: ListingTypeOptions[];
  areas: Models<"AreaResult">[];
  concerns: Models<"ConcernModel">[];
  children: (
    listing: Hit<AlgoliaSearchResult>,
    sendEvent: ReturnType<
      typeof useInfiniteHits<AlgoliaSearchResult>
    >["sendEvent"],
    index: number
  ) => ReactNode;
}

const envName = import.meta.env.REACT_APP_ALGOLIA_ENV;
const algoliaClient = algoliasearch(
  import.meta.env.REACT_APP_ALGOLIA_APP_ID,
  import.meta.env.REACT_APP_ALGOLIA_SEARCH_API_KEY
);

const convertSortOptionToIndexName = (
  sortOption: ListingSortOptions
): string => {
  const baseIndexName = `${envName}_search`;

  switch (sortOption) {
    case "onsale":
      return `${baseIndexName}_on_sale`;
    case "bestsellers":
      return `${baseIndexName}_best_selling`;
    case "priceLowToHigh":
      return `${baseIndexName}_price_asc`;
    case "priceHighToLow":
      return `${baseIndexName}_price_desc`;
    case "recentlyAdded":
      return `${baseIndexName}_recent`;
    default:
      return baseIndexName;
  }
};

const DEFAULT_SORT_OPTION: ListingSortOptions = "featured";

const CustomHits = ({ renderItem }: { renderItem: Props["children"] }) => {
  const { status } = useInstantSearch();
  const { items, isLastPage, showMore, sendEvent } =
    useInfiniteHits<AlgoliaSearchResult>();

  if (status === "error") {
    return (
      <div className="my-3 flex items-center justify-center py-36">
        <div className="text-center text-gray-500">
          <p>Sorry, something went wrong</p>
          <p className="mt-2 text-sm">Please try again later</p>
        </div>
      </div>
    );
  }

  if (!items.length && (status === "loading" || status === "stalled")) {
    return (
      <div className="my-1 flex items-center justify-center py-40">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-color border-b-transparent" />
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="mb-3 mt-2 flex items-center justify-center py-36">
        <div className="text-center text-gray-500">
          <p>No results matched your search.</p>
          <p className="mt-2 text-sm">
            Please try different filters to see more results.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ol className="flex list-none flex-col gap-4">
        {items.map((hit, index) => {
          return (
            <li key={hit.objectID} className="flex flex-col gap-4">
              {renderItem(hit, sendEvent, index)}
            </li>
          );
        })}
      </ol>
      {!isLastPage && (
        <div className="mt-6">
          <Button fullWidth style="primary" onClick={showMore}>
            Load More
          </Button>
        </div>
      )}
    </>
  );
};

const AlgoliaFilterAndSortListings = ({
  platformId,
  types,
  areas,
  concerns,
  filterAndSortState,
}: {
  platformId: string;
  types: ListingTypeOptions[];
  areas: Models<"AreaResult">[];
  concerns: Models<"ConcernModel">[];
  filterAndSortState: ReturnType<typeof useListingFilterAndSort>;
}) => {
  const { status } = useInstantSearch();

  const { refine: refinePlatformId } = useFacet({
    attribute: "platformId",
    limit: 1,
  });

  const {
    refine: refineProductType,
    facetValueToCountMap: productTypeFacets,
    resetFacet: resetProductType,
  } = useFacet({
    attribute: "productType",
    limit: typeOptions.size,
  });

  const {
    refine: refineBodyAreas,
    facetValueToCountMap: bodyAreasFacets,
    resetFacet: resetBodyAreas,
  } = useFacet({
    attribute: "bodyAreaIds",
    limit: areas.length,
  });

  const {
    refine: refineConcerns,
    facetValueToCountMap: concernsFacets,
    resetFacet: resetConcerns,
  } = useFacet({
    attribute: "concernIds",
    limit: concerns.length,
  });

  const { refine: refineSort } = useSortBy({
    items: [
      {
        label: "Default",
        value: convertSortOptionToIndexName("featured"),
      },
      {
        label: "On Sale",
        value: convertSortOptionToIndexName("onsale"),
      },
      {
        label: "Best Sellers",
        value: convertSortOptionToIndexName("bestsellers"),
      },
      {
        label: "Price: Low to High",
        value: convertSortOptionToIndexName("priceLowToHigh"),
      },
      {
        label: "Price: High to Low",
        value: convertSortOptionToIndexName("priceHighToLow"),
      },
      {
        label: "Recently Added",
        value: convertSortOptionToIndexName("recentlyAdded"),
      },
    ],
  });

  const [activeModal, setActiveModal] = useState<
    "concern" | "area" | "type" | "sort" | null
  >(null);

  useEffect(() => {
    refinePlatformId(platformId);
  }, []);

  useEffect(() => {
    const indexName = convertSortOptionToIndexName(filterAndSortState.sort);
    refineSort(indexName);
  }, []);

  useEffect(() => {
    if (filterAndSortState.type) {
      refineProductType(
        convertListingTypeToProductType(filterAndSortState.type)
      );
    }
  }, []);

  useEffect(() => {
    for (const id of filterAndSortState.concerns) {
      refineConcerns(id);
    }
  }, []);

  useEffect(() => {
    for (const id of filterAndSortState.areas) {
      refineBodyAreas(id);
    }
  }, []);

  const filterLabel = (value: string, count: number) =>
    count <= 1 ? value : `${value} +${count - 1}`;

  const isSearching = status === "loading" || status === "stalled";
  const showTreatmentTypeFilter = types.length > 1;

  return (
    <div
      className={cx(
        "flex items-center overflow-x-auto",
        (showTreatmentTypeFilter || !!concernsFacets.size) &&
          "justify-between gap-2"
      )}
    >
      <div className="flex flex-row items-center justify-start">
        {(showTreatmentTypeFilter || !!concernsFacets.size) && (
          <div className="text-body2 text-primary">Filter:</div>
        )}
        {showTreatmentTypeFilter && (
          <TextButton
            endIconSVG={DownChevron}
            onClick={() => {
              setActiveModal("type");
              filterAndSortState.saveCheckpoints();
            }}
          >
            <div className="text-bold2 text-primary">
              {filterAndSortState.type ? (
                typeOptions.get(filterAndSortState.type)
              ) : (
                <>Service&nbsp;Type</>
              )}
            </div>
          </TextButton>
        )}
        {!!concernsFacets.size && (
          <TextButton
            endIconSVG={DownChevron}
            onClick={() => {
              setActiveModal("concern");
              filterAndSortState.saveCheckpoints();
            }}
          >
            <div className="text-bold2 text-primary">
              {filterLabel(
                concerns.find(({ id }) => id === filterAndSortState.concerns[0])
                  ?.name ?? "Concern",
                filterAndSortState.concerns.length
              )}
            </div>
          </TextButton>
        )}
        {!!bodyAreasFacets.size && (
          <TextButton
            endIconSVG={DownChevron}
            onClick={() => {
              setActiveModal("area");
              filterAndSortState.saveCheckpoints();
            }}
          >
            <div className="text-bold2 text-primary">
              {filterLabel(
                areas.find(({ id }) => id === filterAndSortState.areas[0])
                  ?.name ?? "Area",
                filterAndSortState.areas.length
              )}
            </div>
          </TextButton>
        )}
      </div>
      <div className="flex flex-row items-center">
        <div className="text-body2 text-primary">Sort:</div>
        <TextButton
          onClick={() => {
            setActiveModal("sort");
            filterAndSortState.saveCheckpoints();
          }}
        >
          <div className="whitespace-nowrap text-bold2 text-primary">
            {sortOptions.get(filterAndSortState.sort)}
          </div>
        </TextButton>
      </div>
      {activeModal === "type" && (
        <ModalSelectorFilterList
          open={activeModal === "type"}
          onClose={() => {
            setActiveModal(null);
            filterAndSortState.restore();
            resetProductType();
          }}
          title="FILTER BY SERVICE TYPE"
          type="filter"
          items={Array.from(typeOptions).map(([id, title]) => ({
            id,
            title,
            disabled: isSearching || !productTypeFacets.has(id),
          }))}
          checkedItems={filterAndSortState.type ?? []}
          onChange={(id) => {
            if (id == filterAndSortState.type) {
              filterAndSortState.setType(null);
              resetProductType();
              return;
            }

            filterAndSortState.setType(id as ListingTypeOptions);
            refineProductType(id);
          }}
          firstButtonLabel="Apply"
          onFirstButtonClick={() => {
            setActiveModal(null);
            filterAndSortState.setSearchParams();
          }}
          firstButtonDisabled={filterAndSortState.type == null}
          secondButtonLabel="Reset filter"
          onSecondButtonClick={() => {
            setActiveModal(null);
            filterAndSortState.setType(null);
            resetProductType();
          }}
        />
      )}
      {activeModal === "concern" && (
        <ModalSelectorFilterList
          open={activeModal === "concern"}
          onClose={() => {
            setActiveModal(null);
            filterAndSortState.restore();
            resetConcerns();
          }}
          title="FILTER BY CONCERN"
          type="filter"
          items={concerns
            .filter(({ id }) => concernsFacets.has(id))
            .map(({ id, name }) => ({
              id,
              title: name ?? "",
              disabled: isSearching || !concernsFacets.has(id),
            }))}
          checkedItems={filterAndSortState.concerns}
          onChange={(concernId) => {
            filterAndSortState.setConcerns(concernId);
            refineConcerns(concernId);
          }}
          firstButtonLabel="Apply"
          onFirstButtonClick={() => {
            setActiveModal(null);
            filterAndSortState.setSearchParams();
          }}
          firstButtonDisabled={!filterAndSortState.concerns.length}
          secondButtonLabel="Reset filter"
          onSecondButtonClick={() => {
            setActiveModal(null);
            filterAndSortState.clearConcerns();
            resetConcerns();
          }}
        />
      )}

      {activeModal === "area" && (
        <ModalSelectorFilterList
          open={activeModal === "area"}
          onClose={() => {
            setActiveModal(null);
            filterAndSortState.restore();
            resetBodyAreas();
          }}
          title="FILTER BY AREA"
          type="filter"
          items={areas
            .filter(({ id }) => bodyAreasFacets.has(id))
            .map(({ id, name }) => ({
              id,
              title: name ?? "",
              disabled: isSearching || !bodyAreasFacets.has(id),
            }))}
          checkedItems={filterAndSortState.areas}
          onChange={(areaId) => {
            filterAndSortState.setAreas(areaId);
            refineBodyAreas(areaId);
          }}
          firstButtonLabel="Apply"
          onFirstButtonClick={() => {
            setActiveModal(null);
            filterAndSortState.setSearchParams();
          }}
          firstButtonDisabled={!filterAndSortState.areas.length}
          secondButtonLabel="Reset filter"
          onSecondButtonClick={() => {
            setActiveModal(null);
            filterAndSortState.clearAreas();
            resetBodyAreas();
          }}
        />
      )}

      {activeModal === "sort" && (
        <ModalSelectorFilterList
          open={activeModal === "sort"}
          onClose={() => {
            setActiveModal(null);
            filterAndSortState.restore();
          }}
          title="SORT BY"
          type="sort"
          items={Array.from(filterAndSortState.sortOptions).map(
            ([id, title]) => ({
              id,
              title,
            })
          )}
          checkedItems={filterAndSortState.sort}
          onChange={(id) => {
            filterAndSortState.sortBy(id as ListingSortOptions);
          }}
          firstButtonLabel="Apply"
          onFirstButtonClick={() => {
            setActiveModal(null);
            filterAndSortState.setSearchParams();
            refineSort(convertSortOptionToIndexName(filterAndSortState.sort));
          }}
        />
      )}
    </div>
  );
};

export const NextFilterAndSortListings = ({
  platformId,
  types,
  areas,
  concerns,
  children: renderItem,
}: Props) => {
  const filterAndSortState = useListingFilterAndSort(
    [],
    types.length === 1 ? types[0] : null
  );

  return (
    <div className="flex flex-col px-4 py-2">
      <InstantSearch
        insights
        searchClient={algoliaClient}
        indexName={convertSortOptionToIndexName(DEFAULT_SORT_OPTION)}
      >
        <Configure
          hitsPerPage={100}
          facets={["platformId", "productType", "bodyAreaIds", "concernIds"]}
        />
        <AlgoliaFilterAndSortListings
          platformId={platformId}
          areas={areas}
          concerns={concerns}
          filterAndSortState={filterAndSortState}
          types={types}
        />
        <CustomHits renderItem={renderItem} />
      </InstantSearch>
    </div>
  );
};
